// @flow
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import * as React from 'react';
import { withRouter } from 'react-router-dom';
import {
    defineMessages, FormattedMessage, injectIntl, IntlShape,
} from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import { connectAPI, withConfirmation } from 'speed-js-react';
import type { Confirmation } from 'speed-js-react';
import { Box } from 'grommet';
import * as sidePanelActions from 'esm-web-pro/redux/side-panel/actions';
import cableAndFiberMessages from 'esm-web-pro/messages/cableAndFiberMessages';
import Spinner from 'esm-web-pro/components/forms/Spinner';
import type { UUID } from 'esm-web-pro/types/common';
import { getSidePanel } from 'esm-web-pro/redux/side-panel';
import type { SidePanel } from 'esm-web-pro/redux/side-panel/reducer';
import ConnectedCablesList from './ConnectedCablesList';
import TransitCablesList from './TransitCablesList';
import DisconnectedCablesList from './DisconnectedCablesList';
import Point11ConnectSidebar from './Point11ConnectSidebar';

type Props = {
    point: Object,
    // eslint-disable-next-line react/no-unused-prop-types
    updateObject: Function,
    // eslint-disable-next-line react/no-unused-prop-types
    history: Object,
    // eslint-disable-next-line react/no-unused-prop-types
    match: Object,
    intl: IntlShape,
    // eslint-disable-next-line react/no-unused-prop-types
    // objectPartialUpdate: (resourceName: string, id: string, data: Object) => Promise<Object>,
    partialUpdate: (resourceName: string, id: string, data: Object) => Promise<Object>,
    // eslint-disable-next-line react/no-unused-prop-types
    resource: Object,
    confirm: (confirmation: Confirmation) => void,
    editor: BaseEditor,
    onView: Function,
    onRowClick: Function,
    clickedRow: ?string,
    map: Object,
    reload: Function,
    loadedData: boolean,
    split: (id: UUID, data: { point: UUID }) => Promise<Object>,

    connectPanel: SidePanel,
    openPanel: (key: string, url: ?string, sensitive?: true) => { key: string, url: string },
    closePanel: (key: string, ignoreSensitivity?: boolean) => { key: string },
}

export const msg = defineMessages({
    disconnected: {
        id: 'disconnected-cables',
        defaultMessage: 'Disconnected',
    },
    noDisconnected: {
        id: 'no-disconnected-cables',
        defaultMessage: 'No disconnected cables.',
    },
    cantDelete: {
        id: 'cant-delete-point',
        defaultMessage: 'The point is container point for the line/s. You should delete the line/s first.',
    },
    cancelDelete: {
        id: 'delete-point-cancel',
        defaultMessage: 'Cancel',
    },
    cable: {
        id: 'cable-main-info',
        defaultMessage: 'Cable',
    },
    none: {
        id: 'no-termination-point',
        defaultMessage: 'None',
    },
    splitAlertMsg: {
        id: 'split-alert-msg',
        defaultMessage: 'The splitting of the cable is not available yet. It will be released soon.',
    },
    disallowDisconnectMsg: {
        id: 'point11-disallow-disconnect-cable',
        defaultMessage: 'The cable cannot be disconnected because it contains spliced fibers.',
    },
});

type State = {
    editingCable: Object,
    submitting: boolean,
}


const CABLE_CONNECT_KEY = 'CABLE_CONNECT';

const mapStateToProps = (state) => ({
    connectPanel: (() => getSidePanel(state, CABLE_CONNECT_KEY))(),
});

@connect(mapStateToProps, (dispatch) => bindActionCreators(sidePanelActions, dispatch))
@connectAPI((apiClient: ApiClient) => ({
    partialUpdate: {
        send: (resourceName, id, data) => apiClient.resources[resourceName].partialUpdate(id, data),
    },
}))
@withConfirmation
class Point11CableList extends React.Component<Props, State> {
    // connectedCables.reservers - keys are the cable ids , the value is the length of the reserve
    state = {
        editingCable: null,
        submitting: false,
    };

    connectCableTitle = (
        <FormattedMessage
            id="connect-cable-to-point12"
            defaultMessage="Connect cable {ref}"
            values={{
                ref: this.state.editingCable?.ref,
            }}
        />
    );

    componentDidUpdate(prevProps: Readonly<P>, prevState: Readonly<S>, snapshot: SS): void {
        const { connectPanel: prevConnectPanel } = prevProps;
        const { connectPanel } = this.props;

        if (prevConnectPanel && !connectPanel) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ editingCable: null });
        }
    }

    // This is the generic function for both connect and disconnect
    getDisconnectedCableData = (cable) => {
        const text = cable.patch
            ? `${cable.ref} (${cable.fiber_count}, ${(cable.patch && cable.length)} m) `
            : `${cable.ref} (${cable.fiber_count})`;
        return text;
    };

    getConnectedCableData = (cable) => (
        (`${cable.ref} (${cable.fiber_count}/${Number(cable.spliced_fibers_count)})`)
    );

    getTransitCableData = (cable) => (`${cable.ref} (${cable.fiber_count}, ${cable.reserve || '0'} m) `);

    handleCustomError = (data) => {
        const { confirm, intl: { formatMessage } } = this.props;

        if (data) {
            confirm({
                message: data,
                actions: {
                    cancel: {
                        label: formatMessage(msg.cancelDelete),
                        callback: () => {
                        },
                        type: 'negative',
                    },
                },
            });
        }
    };

    handleDisconnectCable = (cable: Object) => {
        const {
            partialUpdate, intl: { formatMessage }, confirm, reload,
        } = this.props;
        const {
            spliced_fibers_start: splicedFibersStart, spliced_fibers_end: splicedFibersEnd,
            is_parent_start_termination: isParentStartTermination,
        } = cable;
        const data = {};

        if (splicedFibersStart || splicedFibersEnd) {
            confirm({
                message: formatMessage(msg.disallowDisconnectMsg),
                actions: {
                    cancel: {
                        label: formatMessage(msg.cancelDelete),
                        callback: () => {
                        },
                        type: 'negative',
                    },
                },
            });
        } else {
            this.setState({ submitting: true });
            if (isParentStartTermination) {
                data.start_termination_point = null;
            } else {
                data.end_termination_point = null;
            }

            partialUpdate('cable', cable.uuid, data).then((res) => {
                if (res) {
                    reload();
                    this.setState({ submitting: false });
                }
            });
        }
    };

    handleConnectCable = (layer12Id) => {
        this.setState({ submitting: true });
        const { editingCable } = this.state;
        if (!editingCable) return;

        const { point, partialUpdate, reload } = this.props;
        const data = {};

        const points12 = point.connected_cables.layers1_2;
        const points12Keys = Object.keys(point.connected_cables.layers1_2);
        const layer12Key: Object = points12Keys.find((key: string) => points12[key].id === Number(layer12Id));
        const layer12 = points12[layer12Key];

        if (editingCable.start_container_point.id === point.id) {
            data.start_termination_point = layer12.id;
        } else if (editingCable.end_container_point.id === point.id) {
            data.end_termination_point = layer12.id;
        } else {
            console.error('The cable does not have the proper container point');
        }

        partialUpdate('cable', editingCable.uuid, data).then(() => {
            reload();
            this.setState({ submitting: false });
            this.setConnectContent(false);
        });
    };

    handleSplitCable = (cable) => {
        const {
            split, point, reload, editor,
        } = this.props;

        this.setState({ submitting: true });

        split(cable.uuid, { point: point.uuid }).then(() => {
            this.setState({ submitting: false });
            reload();
            editor.focus();
        });
    };

    setConnectContent = (boolean, editingCable = null) => {
        const { openPanel, match, closePanel } = this.props;

        if (editingCable) {
            openPanel(CABLE_CONNECT_KEY, this.connectCableTitle, match.url, true);
        } else {
            closePanel(CABLE_CONNECT_KEY, true);
        }
        this.setState({ editingCable });
    };

    handleConnectClose = () => {
        const { closePanel } = this.props;

        closePanel(CABLE_CONNECT_KEY, false);
    };

    render() {
        const {
            point, map, editor, onView, onRowClick, clickedRow, loadedData,
        } = this.props;
        const { submitting, editingCable } = this.state;
        const mapIsDraft = map && map.is_draft;
        const {
            connected_cables: connectedCables,
            disconnected_cables: disconnectedCables, transit_cables: transitCables,
        } = point;

        return (
            <>
                <Spinner
                    type="clipLoader"
                    loading={!loadedData || submitting}
                    blocking={false}
                    color="black"
                    coverStyle={{
                        position: 'relative',
                        width: 'auto',
                        height: 'auto',
                        top: '10px',
                        padding: '30px',
                    }}
                />
                { (loadedData && !submitting) && (
                    <Box
                        key={point.uuid}
                        height={{ min: '100%' }}
                    >
                        {point.type !== 'fork' && (
                            <Box>
                                <ConnectedCablesList
                                    key={`connected-cable-list-${point.id}`}
                                    cables={connectedCables}
                                    onDisconnectCable={this.handleDisconnectCable}
                                    getCableData={this.getConnectedCableData}
                                    mapIsDraft={mapIsDraft}
                                    editor={editor}
                                    onView={onView}
                                    point11={point}
                                    onRowClick={onRowClick}
                                    clickedRow={clickedRow}
                                />
                            </Box>
                        )}
                        {point.type !== 'fork' && (
                            <Box pad={{ top: 'small' }}>
                                <DisconnectedCablesList
                                    cables={disconnectedCables}
                                    onConnectCable={this.setConnectContent}
                                    getCableData={this.getDisconnectedCableData}
                                    mapIsDraft={mapIsDraft}
                                    editor={editor}
                                    point11={point}
                                    onRowClick={onRowClick}
                                    clickedRow={clickedRow}
                                />
                            </Box>
                        )}
                        <Box pad={{ top: 'small' }}>
                            <TransitCablesList
                                cables={transitCables}
                                onSplitCable={this.handleSplitCable}
                                getCableData={this.getTransitCableData}
                                mapIsDraft={mapIsDraft}
                                editor={editor}
                                onPoint11View={onView}
                                point11={point}
                                onRowClick={onRowClick}
                                clickedRow={clickedRow}
                            />
                        </Box>
                        {editingCable && (
                            <Point11ConnectSidebar
                                point={point}
                                onClose={this.handleConnectClose}
                                connectCable={this.handleConnectCable}
                                id={CABLE_CONNECT_KEY}
                                title={this.connectCableTitle}
                            />
                        )}
                    </Box>
                )}
            </>
        );
    }
}

const apiCallback = (apiClient: ApiClient) => ({
    split: {
        send: (id: UUID, data: { point: UUID }) => apiClient.resources.cable.split(id, data),
    },
});

export default connectAPI(apiCallback)(withRouter(injectIntl(Point11CableList)));
